import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import SupportAgentRoundedIcon from '@mui/icons-material/SupportAgentRounded';
import {
  Box,
  Checkbox,
  CircularProgress,
  Container,
  Fade,
  FormControlLabel,
  Grid,
  IconButton,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { DateTime } from 'luxon'; // Import Luxon for date handling
import React, { Suspense, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useGetBuildingsByAccountQuery } from '../../../api/recalc/endpoints/buildingApiSlice';
import {
  useGetSubscriptionsByAccountQuery,
  useUpdateManualRunRequestMutation,
  useUpdateSubscriptionMutation,
} from '../../../api/recalc/endpoints/jobSubscriptionApiSlice';
import { getSelectedAccount } from '../../../redux/selectedAccountSlice';
import { showSnackbar } from '../../../redux/snackbarSlice';

import { useNavigate } from 'react-router-dom';
import HtmlTooltip from '@/components/tooltip/HtmlTooltip';

import EventCalendar from '@/features/calendar/components/EventCalendar';
import StaticCalendarDatePicker from '@/features/calendar/components/StaticCalendarDatePicker';

const CalendarPage = () => {
  const selectedAccount = useSelector(getSelectedAccount);
  const { data: subscriptions, isSuccess: isSuccessSubscriptions } =
    useGetSubscriptionsByAccountQuery(selectedAccount?.AccountId, {
      skip: !selectedAccount?.AccountId,
    });
  const { data: buildings, isSuccess: isSuccessBuildings } =
    useGetBuildingsByAccountQuery(selectedAccount?.AccountId, {
      skip: !selectedAccount?.AccountId,
    });
  const [updateSubscription] = useUpdateSubscriptionMutation();
  const [
    manualRunRequest,
    {
      isSuccess: manualRunRequestIsSuccess,
      isLoading: isLoadingManualRequest,
      isError: manualRunRequestIsError,
      error: manualRunRequestError,
    },
  ] = useUpdateManualRunRequestMutation();

  const [filteredSubscriptions, setFilteredSubscriptions] = useState([]);
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]); // State for filtered events
  const [selectedSubscriptions, setSelectedSubscriptions] = useState({}); // Initialize empty
  const [selectedDate, setSelectedDate] = useState(DateTime.now()); // State for selected date
  const [currentViewDate, setCurrentViewDate] = useState(DateTime.now()); // State for current view date

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleManualRunRequest = async ({ subscriptionId }) => {
    try {
      const response = await manualRunRequest({ id: subscriptionId }).unwrap();
      dispatch(
        showSnackbar({
          message: 'Manual run request was sent',
          severity: 'success',
          response: JSON.stringify(response),
        }),
      );
    } catch (error) {
      dispatch(
        showSnackbar({
          message: 'Error sending manual run request',
          severity: 'error',
          response: error.message,
        }),
      );
    }
  };

  const handleUpdateSubscription = async ({ subscriptionId, data }) => {
    try {
      const updatedData = {
        DataInput: data.DataInput, // Preserve DataInput
        UserInput: data.UserInput, // Updated UserInput with new events
        AlgorithmInput: data.AlgorithmInput, // Preserve AlgorithmInput
        DisplayName: data.DisplayName, // Preserve DisplayName
      };

      // Call the API to update the subscription
      const response = await updateSubscription({
        id: subscriptionId,
        patch: updatedData,
      }).unwrap();

      dispatch(
        showSnackbar({
          message: 'Subscription was updated',
          severity: 'success',
          response: JSON.stringify(response),
        }),
      );
    } catch (error) {
      dispatch(
        showSnackbar({
          message: 'Error updating subscription',
          severity: 'error',
          response: error.message,
        }),
      );
    }
  };

  const handleSupportClick = () => {
    navigate('/support');
  };

  useEffect(() => {
    if (subscriptions) {
      const predictiveSubscriptions = subscriptions.filter(
        (sub) => sub.JobType === 'PredictiveHeatControl',
      );
      setFilteredSubscriptions(predictiveSubscriptions);

      const preparedEvents = predictiveSubscriptions.flatMap((sub) => {
        const building = buildings?.find(
          (b) => b.BuildingId === sub.BuildingId,
        );
        const buildingName = building?.Name || 'Unknown Building';
        const buildingEmptyEvents = sub.UserInput.BuildingEmptyCalendar.map(
          (event, index) => ({
            id: `${sub.JobSubscriptionId}-empty-${index}`,
            title: `${event.Name}`,
            start: event.From,
            end: event.To,
            color: 'hsl(10, 85%, 47%)',
            subscriptionId: sub.JobSubscriptionId,
            subscriptionDisplayName: `${sub.DisplayName}`,
            buildingName: buildingName,
            type: 'empty',
          }),
        );

        const buildingOccupiedEvents =
          sub.UserInput.BuildingOccupiedCalendar.map((event, index) => ({
            id: `${sub.JobSubscriptionId}-occupied-${index}`,
            title: `${event.Name}: ${buildingName} - ${sub.DisplayName}`,
            eventName: `${event.Name}`,
            start: event.From,
            end: event.To,
            color: 'hsl(63, 100%, 40%)',
            subscriptionId: sub.JobSubscriptionId,
            subscriptionDisplayName: sub.DisplayName,
            buildingName: buildingName,
            type: 'occupied',
          }));

        return [...buildingEmptyEvents, ...buildingOccupiedEvents];
      });

      setEvents(preparedEvents);

      // Initialize selectedSubscriptions to select all active subscriptions by default
      const initialSelectedSubscriptions = {};
      predictiveSubscriptions.forEach((sub) => {
        const buildingId = sub.BuildingId;
        if (!initialSelectedSubscriptions[buildingId]) {
          initialSelectedSubscriptions[buildingId] = [];
        }

        // Only select subscriptions that are active
        if (sub.StateCode === 'Active') {
          initialSelectedSubscriptions[buildingId].push(sub.JobSubscriptionId);
        }
      });
      setSelectedSubscriptions(initialSelectedSubscriptions);
    }
  }, [subscriptions]);

  // Filter events based on selected subscriptions
  useEffect(() => {
    const selectedSubscriptionIds = Object.values(selectedSubscriptions).flat();
    const filtered = events.filter((event) =>
      selectedSubscriptionIds.includes(event.subscriptionId),
    );
    setFilteredEvents(filtered);
  }, [events, selectedSubscriptions]);

  const handleBuildingSelectAllToggle = (buildingId, e) => {
    const activeSubscriptions = enrichedBuildings
      .find((b) => b.BuildingId === buildingId)
      .enrichedSubscriptions.filter((sub) => sub.StateCode === 'Active') // Only consider active subscriptions
      .map((sub) => sub.JobSubscriptionId);

    if (e.target.checked) {
      setSelectedSubscriptions((prev) => ({
        ...prev,
        [buildingId]: activeSubscriptions,
      }));
    } else {
      setSelectedSubscriptions((prev) => ({
        ...prev,
        [buildingId]: [],
      }));
    }
  };

  const handleSubscriptionToggle = (buildingId, subscriptionId, isActive) => {
    // If the subscription is inactive, do nothing
    if (!isActive) return;

    setSelectedSubscriptions((prev) => {
      const currentSubscriptions = prev[buildingId] || [];

      const updatedSubscriptions = currentSubscriptions.includes(subscriptionId)
        ? currentSubscriptions.filter((id) => id !== subscriptionId)
        : [...currentSubscriptions, subscriptionId];

      return {
        ...prev,
        [buildingId]: updatedSubscriptions,
      };
    });
  };

  const handleSelectAllToggle = () => {
    const allSelected =
      Object.keys(selectedSubscriptions).length === enrichedBuildings.length &&
      enrichedBuildings.every(
        (building) =>
          selectedSubscriptions[building.BuildingId]?.length ===
          building.enrichedSubscriptions.filter(
            (sub) => sub.StateCode === 'Active',
          ).length,
      );

    if (allSelected) {
      // If all buildings and active subscriptions are selected, deselect all
      setSelectedSubscriptions({});
    } else {
      // Otherwise, select all active subscriptions
      const allSelectedSubscriptions = {};
      enrichedBuildings.forEach((building) => {
        const activeSubscriptions = building.enrichedSubscriptions
          .filter((sub) => sub.StateCode === 'Active')
          .map((sub) => sub.JobSubscriptionId);
        allSelectedSubscriptions[building.BuildingId] = activeSubscriptions;
      });
      setSelectedSubscriptions(allSelectedSubscriptions);
    }
  };

  const handleDateChange = (newDate) => {
    setSelectedDate(newDate); // Update the selected date state
    setCurrentViewDate(newDate); // Update the current view date state for syncing with FullCalendar
  };

  const handleMonthChange = (newDate) => {
    setCurrentViewDate(newDate); // Update current view date state for syncing with FullCalendar
  };

  const enrichedBuildings =
    isSuccessSubscriptions &&
    buildings?.map((building) => ({
      ...building,
      enrichedSubscriptions: subscriptions.filter(
        (sub) =>
          sub.JobType === 'PredictiveHeatControl' &&
          sub.BuildingId === building.BuildingId,
      ),
    }));

  const allSelected =
    Object.keys(selectedSubscriptions)?.length === enrichedBuildings?.length &&
    enrichedBuildings.every(
      (building) =>
        selectedSubscriptions[building.BuildingId]?.length ===
        building.enrichedSubscriptions.length,
    );

  return (
    <Container maxWidth="xxl">
      {isSuccessBuildings &&
      buildings?.length > 0 &&
      subscriptions?.length > 0 ? (
        <Suspense
          fallback={
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '80vh',
              }}
            >
              <CircularProgress />
            </Box>
          }
        >
          <Grid container spacing={2}>
            {/* Left Side - Static Calendar and Checkboxes in a Stack */}
            <Grid
              item
              xs={12}
              md={4.5}
              lg={3.5}
              xl={2.5}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                minHeight: '600px',
                overflow: 'hidden',
              }}
            >
              <Stack spacing={2} sx={{ height: '100%' }}>
                <Box sx={{ display: 'flex', justifyContent: 'start' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {/* <Typography variant="h5" color="textPrimary" fontWeight={700}>Kalender</Typography> */}
                    <ListItemText
                      primary="Kalender"
                      primaryTypographyProps={{
                        variant: 'h5',
                        color: 'text.primary',
                        fontWeight: 700,
                      }}
                      secondary="Planlægning af særlig begivenheder"
                      // secondary="Brug kalenderen til at oplyse om begivenheder udenfor jeres almindelige brug af bygningen, som vores algoritme ikke kan forudsige fx jeres julefrokost eller et ualmindeligt weekend-arrangement. Tilføj også gerne når bygningen ikke er i brug fx ved juleferie. Tilføj begivnheder for hele eller dele af bygningen."
                      sx={{ ml: 2 }}
                    />
                    <Tooltip
                      title="Brug kalenderen til at oplyse om begivenheder udenfor jeres almindelige brug af bygningen, som vores algoritme ikke kan forudsige fx jeres julefrokost eller et ualmindeligt weekend-arrangement. Tilføj også gerne når bygningen ikke er i brug fx ved juleferie. Tilføj begivnheder for hele eller dele af bygningen."
                      placement="top"
                      arrow
                    >
                      <InfoRoundedIcon
                        sx={{
                          color: (theme) =>
                            theme.palette.mode === 'light'
                              ? 'lightgray'
                              : 'gray',
                          ml: 1,
                        }}
                        fontSize="small"
                      />
                    </Tooltip>
                  </Box>
                </Box>
                {/* Calendar Picker */}
                <StaticCalendarDatePicker
                  selectedDate={selectedDate}
                  onDateChange={handleDateChange}
                  onMonthChange={handleMonthChange}
                />

                {/* Building Selection Checkboxes */}
                <Box sx={{ overflowY: 'auto', pl: 2, maxWidth: '320px' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'left',
                      mb: 1,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'start',
                        mb: 1,
                        justifyContent: 'start',
                      }}
                    >
                      <>
                        {/* <Typography component="div" variant="subtitle2" color="text.secondary">
                      Vis/skjul kalender
                    </Typography> */}
                        <Typography
                          sx={{
                            // opacity: '0',
                            // ml: 8,
                            // fontSize: '0.9rem',
                            ':hover': { cursor: 'pointer', opacity: '1' },
                            transition: 'opacity 0.3s',
                          }}
                          component="a"
                          onClick={handleSelectAllToggle}
                        >
                          {allSelected
                            ? 'Skjul alle kalendere'
                            : 'Vis alle kalendere'}
                        </Typography>
                        <HtmlTooltip
                          title="Vi har forsøgt at lave navngivningen af de enkelte blandesløjfer gennemskuelig. Skulle I have ønsker til at få ændret navnet, så det er nemmere at lave begivenheder, som kun gælder dele af bygningen, så ræk ud til vores support"
                          content={
                            <Box>
                              <IconButton
                                sx={{ borderRadius: 1, mr: 1 }}
                                onClick={handleSupportClick}
                              >
                                <SupportAgentRoundedIcon fontSize="small" />
                              </IconButton>
                              (tryk på ikonet for at gå til support siden)
                            </Box>
                          }
                        >
                          <InfoRoundedIcon
                            sx={{
                              color: (theme) =>
                                theme.palette.mode === 'light'
                                  ? 'lightgray'
                                  : 'gray',
                              ml: 1,
                            }}
                            fontSize="small"
                          />
                        </HtmlTooltip>
                      </>
                    </Box>
                  </Box>
                  {enrichedBuildings.map((building) => {
                    // Check if all subscriptions for this building are inactive
                    const allInactive = building.enrichedSubscriptions.every(
                      (sub) => sub.StateCode !== 'Active',
                    );

                    const allSelected =
                      selectedSubscriptions[building.BuildingId]?.length ===
                      building.enrichedSubscriptions.filter(
                        (sub) => sub.StateCode === 'Active',
                      ).length;

                    const someSelected =
                      selectedSubscriptions[building.BuildingId]?.length > 0 &&
                      selectedSubscriptions[building.BuildingId]?.length <
                        building.enrichedSubscriptions.filter(
                          (sub) => sub.StateCode === 'Active',
                        ).length;

                    return (
                      <Box key={building.BuildingId} sx={{ mb: 2 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={allSelected}
                              indeterminate={someSelected}
                              onChange={(e) =>
                                handleBuildingSelectAllToggle(
                                  building.BuildingId,
                                  e,
                                )
                              }
                              disabled={allInactive} // Disable building selection if all subscriptions are inactive
                            />
                          }
                          label={
                            allInactive ? (
                              <Typography variant="body2" color="textSecondary">
                                {building.Name + ' - Pauset/Inaktiv'}
                              </Typography>
                            ) : (
                              <Typography variant="body2">
                                {building.Name}
                              </Typography>
                            )
                          }
                          sx={{ fontWeight: 'bold' }}
                        />

                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            ml: 3,
                          }}
                        >
                          {building.enrichedSubscriptions.map(
                            (subscription) => (
                              <FormControlLabel
                                key={subscription.JobSubscriptionId}
                                control={
                                  <Checkbox
                                    checked={
                                      selectedSubscriptions[
                                        building.BuildingId
                                      ]?.includes(
                                        subscription.JobSubscriptionId,
                                      ) || false
                                    }
                                    onChange={() =>
                                      handleSubscriptionToggle(
                                        building.BuildingId,
                                        subscription.JobSubscriptionId,
                                        subscription.StateCode === 'Active', // Pass active status
                                      )
                                    }
                                    disabled={
                                      subscription.StateCode !== 'Active'
                                    } // Disable if subscription is not active
                                  />
                                }
                                label={
                                  subscription.StateCode === 'Active' ? (
                                    <Typography variant="body2">
                                      {subscription.DisplayName}
                                    </Typography>
                                  ) : (
                                    <Typography
                                      variant="body2"
                                      color="textSecondary"
                                    >
                                      {`${subscription.DisplayName} - Ingen styring`}
                                    </Typography>
                                  )
                                }
                              />
                            ),
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Stack>
            </Grid>

            {/* Right Side - Event Calendar */}
            <Grid
              item
              xs={12}
              md={7.5}
              lg={8.5}
              xl={9.5}
              sx={
                {
                  // minHeight: '600px',
                  // overflow: 'hidden',
                }
              }
            >
              {/* {isSuccessBuildings && buildings?.length > 0 && subscriptions?.length > 0 ? ( */}
              <Fade in={true} timeout={500}>
                <Box sx={{ mb: 4 }}>
                  <EventCalendar
                    subscriptions={filteredSubscriptions}
                    buildings={buildings}
                    events={filteredEvents}
                    selectedDate={selectedDate}
                    currentViewDate={currentViewDate}
                    handleSupportClick={handleSupportClick}
                    onDateSelect={handleDateChange}
                    updateSubscription={handleUpdateSubscription}
                    manualRunRequest={handleManualRunRequest}
                    selectedSubscriptions={selectedSubscriptions}
                  />
                </Box>
              </Fade>
            </Grid>
          </Grid>
        </Suspense>
      ) : (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '80vh',
          }}
        >
          <Typography variant="body2" color="textSecondary">
            Ingen data tilgængelig. Se{' '}
            <Typography
              variant="a"
              component="a"
              sx={{ ':hover': { cursor: 'pointer' } }}
              onClick={() => navigate('/support')}
            >
              support
            </Typography>{' '}
            for yderligere information.
          </Typography>
        </Box>
      )}
    </Container>
  );
};

export default CalendarPage;
